import { BotaoVoltar } from "@/shared/components/BotaoVoltar";
import { useEffect, useState } from "react";
import { Button } from "@/shared/components/Button";
import { Modal } from "@/shared/components/Modal";
import { CampoSelect, CampoTexto } from "@/modules/visitantes/components/Campos";
import {
  useMinisteriosQueLidero,
  usePassarInformacao,
} from "@/modules/visitantes/hooks/useVisitantes";

interface Props {
  visitante: { id: string; nome: string } | null;
  onClose: () => void;
  onEnviada: () => void;
}

/** O líder/gestor de outro ministério passa uma informação útil sobre o visitante ao ministério responsável. */
export function ModalPassarInformacao({ visitante, onClose, onEnviada }: Props) {
  const passar = usePassarInformacao();
  const { data: ministerios = [] } = useMinisteriosQueLidero(!!visitante);
  const [texto, setTexto] = useState("");
  const [ministerioId, setMinisterioId] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (visitante) {
      setTexto("");
      setMinisterioId("");
      setErro(null);
    }
  }, [visitante]);

  async function enviar() {
    if (!visitante) return;
    setErro(null);
    if (texto.trim().length < 1) {
      setErro("Escreva a informação.");
      return;
    }
    try {
      await passar.mutateAsync({
        pessoaId: visitante.id,
        texto,
        ministerioId: ministerioId || undefined,
      });
      onEnviada();
      onClose();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível passar a informação.");
    }
  }

  return (
    <Modal
      titulo="Passar informação"
      aberto={!!visitante}
      onClose={onClose}
      alterado={texto.trim() !== "" || ministerioId !== ""}
    >
      <p className="mb-4 text-sm text-muted">
        Sabe algo que ajuda no cuidado de {visitante?.nome}? O ministério responsável recebe o seu
        recado.
      </p>
      <div className="flex flex-col gap-4">
        {ministerios.length > 0 && (
          <CampoSelect
            label="Em nome de qual ministério? (opcional)"
            name="ministerioInfo"
            value={ministerioId}
            onChange={(e) => setMinisterioId(e.target.value)}
          >
            <option value="">Nenhum em especial</option>
            {ministerios.map((m) => (
              <option key={m.id} value={m.id}>
                {m.nome}
              </option>
            ))}
          </CampoSelect>
        )}
        <CampoTexto
          label="Informação"
          name="textoInfo"
          rows={4}
          maxLength={500}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
        />
      </div>
      {erro && (
        <p role="alert" className="mt-3 rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
          {erro}
        </p>
      )}
      <div className="mt-5 flex justify-end gap-2">
        <BotaoVoltar onClick={onClose} className="min-h-11" />
        <Button type="button" onClick={enviar} carregando={passar.isPending} className="min-h-11">
          Enviar
        </Button>
      </div>
    </Modal>
  );
}
